import {
  computed,
  ref,
} from 'vue'

import type { FineUploaderModalUploadHelpers } from '@/composables/current-student-advanced-standing-form/fineUploaderModalUploadHelpers'
import { useLegacyAutocomplete } from '@/composables/current-student-advanced-standing-form/useLegacyAutocomplete'
import { useUploadAttachmentState } from '@/composables/current-student-advanced-standing-form/useUploadAttachmentState'
import {
  clearModalAlert,
  showModalError,
  type AlertTone,
} from '@/models/current-student-advanced-standing-form/modalAlert'
import {
  customInstitutionState,
  externalCourseForEdit,
  initialExternalCourseModalFormState,
} from '@/models/current-student-advanced-standing-form/externalCourseModal'
import {
  displayInstitution,
} from '@/models/current-student-advanced-standing-form/display'
import type {
  Attachment,
  ExternalCourse,
  FineUploaderUpload,
  Institution,
  InstitutionListQuery,
  InstitutionListResponse,
} from '@/models/current-student-advanced-standing-form/api'

type UseExternalCourseModalControllerOptions = {
  maxAttachments: number
  searchInstitutions: (query: InstitutionListQuery) => Promise<InstitutionListResponse>
  uploadHelpers: FineUploaderModalUploadHelpers
}

export function useExternalCourseModalController({
  maxAttachments,
  searchInstitutions,
  uploadHelpers,
}: UseExternalCourseModalControllerOptions) {
  const externalCourseModalVisible = ref(false)
  const externalCourseSaving = ref(false)
  const externalCourseModalError = ref('')
  const externalCourseModalAlertTone = ref<AlertTone>('danger')
  const currentExternalCourse = ref<ExternalCourse | null>(null)
  const externalCourseInstitution = ref<Institution | null>(null)
  const externalCourseInstitutionName = ref<string | undefined>()
  const institutionSearchText = ref('')
  const institutionSearchTimer = ref<number | undefined>()
  const externalCourseName = ref('')
  const externalCourseCode = ref('')
  const externalCourseLevelId = ref('')
  const externalCourseCompletedYear = ref('')
  const courseAttachmentDeleteIds = ref<number[]>([])
  const courseNewAttachments = ref<FineUploaderUpload[]>([])
  const courseUploadsInProgress = ref(0)
  const courseUploadDropActive = ref(false)
  const courseUploadAlertMessage = ref('')

  const {
    suggestions: institutionSuggestions,
    activeIndex: institutionActiveIndex,
    setSuggestions: setInstitutionSuggestions,
    clearSuggestions: clearInstitutionSuggestions,
    resetActiveIndex: resetInstitutionActiveIndex,
    handleKeydown: handleInstitutionAutocompleteKeydown,
  } = useLegacyAutocomplete<Institution>({
    selectItem: selectInstitution,
  })

  const currentCourseAttachments = computed(() => {
    return currentExternalCourse.value?.external_course_attachments ?? []
  })

  const {
    uploadItemLimit: courseUploadItemLimit,
    activeNewUploadCount: activeCourseNewUploadCount,
    canUploadAttachment: canUploadCourseAttachment,
    visibleNewAttachments: visibleCourseNewAttachments,
    showFineUploader: showCourseFineUploader,
  } = useUploadAttachmentState({
    savedAttachments: currentCourseAttachments,
    deletedAttachmentIds: courseAttachmentDeleteIds,
    newAttachments: courseNewAttachments,
    maxAttachments,
  })

  function openAddExternalCourseModal(): void {
    openExternalCourseModal(null)
  }

  function openEditExternalCourseModal(course: ExternalCourse): void {
    openExternalCourseModal(externalCourseForEdit(course))
  }

  function openExternalCourseModal(course: ExternalCourse | null): void {
    const formState = initialExternalCourseModalFormState(course)

    currentExternalCourse.value = formState.currentExternalCourse
    clearModalAlert(externalCourseModalError, externalCourseModalAlertTone)
    externalCourseInstitution.value = formState.externalCourseInstitution
    externalCourseInstitutionName.value = formState.externalCourseInstitutionName
    institutionSearchText.value = formState.institutionSearchText
    clearInstitutionSuggestions()
    externalCourseName.value = formState.externalCourseName
    externalCourseCode.value = formState.externalCourseCode
    externalCourseLevelId.value = formState.externalCourseLevelId
    externalCourseCompletedYear.value = formState.externalCourseCompletedYear
    courseAttachmentDeleteIds.value = []
    uploadHelpers.revokeUploadThumbnailsFor(courseNewAttachments.value)
    courseNewAttachments.value = []
    courseUploadsInProgress.value = 0
    externalCourseModalVisible.value = true
  }

  function closeExternalCourseModal(): void {
    uploadHelpers.revokeUploadThumbnailsFor(courseNewAttachments.value)
    externalCourseModalVisible.value = false
  }

  async function onInstitutionSearchInput(): Promise<void> {
    resetInstitutionActiveIndex()
    if (institutionSearchText.value.length < 2) {
      clearInstitutionSuggestions()
      return
    }

    if (institutionSearchTimer.value != null) {
      window.clearTimeout(institutionSearchTimer.value)
    }

    institutionSearchTimer.value = window.setTimeout(() => {
      void loadInstitutionSuggestions(institutionSearchText.value)
    }, 300)
  }

  async function loadInstitutionSuggestions(search: string): Promise<void> {
    try {
      const result = await searchInstitutions({
        show_empty_institutions: true,
        search,
        limit: 20,
      })
      setInstitutionSuggestions(result.data.map((institution) => ({
        ...institution,
        value: displayInstitution(institution),
      })))
    } catch {
      clearInstitutionSuggestions()
    }
  }

  function selectInstitution(institution: Institution): void {
    externalCourseInstitution.value = institution
    externalCourseInstitutionName.value = undefined
    institutionSearchText.value = displayInstitution(institution)
    clearInstitutionSuggestions()
  }

  function blurInstitutionSearch(): void {
    window.setTimeout(() => {
      if (institutionSearchText.value === '') {
        externalCourseInstitution.value = null
      }
      institutionSearchText.value = displayInstitution(externalCourseInstitution.value)
      clearInstitutionSuggestions()
    }, 10)
  }

  function onInstitutionSearchKeydown(event: KeyboardEvent): void {
    if (handleInstitutionAutocompleteKeydown(event)) {
      return
    }
  }

  function useCustomInstitution(): void {
    const institutionName = window.prompt('Please enter the name of the institution')
    if (institutionName == null) return

    const formState = customInstitutionState(institutionName)
    externalCourseInstitution.value = formState.externalCourseInstitution
    externalCourseInstitutionName.value = formState.externalCourseInstitutionName
    institutionSearchText.value = formState.institutionSearchText
  }

  function closeCourseUploadAlert(): void {
    uploadHelpers.closeFineUploaderAlert('#courseAttachmentFineUploader', courseUploadAlertMessage)
  }

  function onCourseUploadDragEnter(event: DragEvent): void {
    uploadHelpers.onUploadDragEnter(event, courseUploadDropActive)
  }

  function onCourseUploadDragOver(event: DragEvent): void {
    uploadHelpers.onUploadDragOver(event, courseUploadDropActive)
  }

  function onCourseUploadDragLeave(event: DragEvent): void {
    uploadHelpers.onUploadDragLeave(event, courseUploadDropActive)
  }

  async function onCourseUploadDrop(event: DragEvent): Promise<void> {
    await uploadHelpers.onUploadDrop(event, courseUploadDropActive, uploadCourseAttachmentFiles)
  }

  async function showCourseUploadLimitAlert(addedItems = 1): Promise<void> {
    await uploadHelpers.showUploadLimitAlert(
      '#courseAttachmentFineUploader',
      courseUploadAlertMessage,
      activeCourseNewUploadCount.value,
      courseUploadItemLimit.value,
      addedItems,
    )
  }

  async function onCourseAttachmentSelected(event: Event): Promise<void> {
    await uploadHelpers.onAttachmentSelected(event, uploadCourseAttachmentFiles)
  }

  async function uploadCourseAttachmentFiles(files: File[]): Promise<void> {
    if (files.length === 0) return

    if (activeCourseNewUploadCount.value + files.length > courseUploadItemLimit.value) {
      await showCourseUploadLimitAlert(files.length)
      return
    }

    for (const file of files) {
      await uploadCourseAttachmentFile(file)
    }
  }

  async function uploadCourseAttachmentFile(file: File): Promise<void> {
    if (!canUploadCourseAttachment.value) {
      await showCourseUploadLimitAlert()
      return
    }

    await uploadHelpers.uploadAttachment(file, courseNewAttachments, courseUploadsInProgress, (message) => {
      showModalError(externalCourseModalError, externalCourseModalAlertTone, message)
    })
  }

  function stageCourseAttachmentDelete(attachment: Attachment): void {
    if (!courseAttachmentDeleteIds.value.includes(attachment.attachment_id)) {
      courseAttachmentDeleteIds.value.push(attachment.attachment_id)
    }
  }

  async function deleteStagedCourseAttachment(attachment: FineUploaderUpload): Promise<void> {
    await uploadHelpers.deleteStagedUpload(attachment, (message) => {
      showModalError(externalCourseModalError, externalCourseModalAlertTone, message)
    })
  }

  async function retryCourseAttachment(attachment: FineUploaderUpload): Promise<void> {
    await uploadHelpers.retryUploadAttachment(attachment, courseUploadsInProgress, (message) => {
      showModalError(externalCourseModalError, externalCourseModalAlertTone, message)
    })
  }

  return {
    externalCourseModalVisible,
    externalCourseSaving,
    externalCourseModalError,
    externalCourseModalAlertTone,
    currentExternalCourse,
    externalCourseInstitution,
    externalCourseInstitutionName,
    institutionSearchText,
    institutionSearchTimer,
    externalCourseName,
    externalCourseCode,
    externalCourseLevelId,
    externalCourseCompletedYear,
    courseAttachmentDeleteIds,
    courseNewAttachments,
    courseUploadsInProgress,
    courseUploadDropActive,
    courseUploadAlertMessage,
    institutionSuggestions,
    institutionActiveIndex,
    currentCourseAttachments,
    visibleCourseNewAttachments,
    showCourseFineUploader,
    openAddExternalCourseModal,
    openEditExternalCourseModal,
    openExternalCourseModal,
    closeExternalCourseModal,
    onInstitutionSearchInput,
    loadInstitutionSuggestions,
    selectInstitution,
    blurInstitutionSearch,
    onInstitutionSearchKeydown,
    useCustomInstitution,
    closeCourseUploadAlert,
    onCourseUploadDragEnter,
    onCourseUploadDragOver,
    onCourseUploadDragLeave,
    onCourseUploadDrop,
    showCourseUploadLimitAlert,
    onCourseAttachmentSelected,
    uploadCourseAttachmentFiles,
    uploadCourseAttachmentFile,
    stageCourseAttachmentDelete,
    deleteStagedCourseAttachment,
    retryCourseAttachment,
  }
}
